import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * A portal-rendered modal that escapes any parent stacking-context
 * (sticky topbars with backdrop-blur, transformed ancestors, etc).
 * Centers content on the viewport with a dimmed, blurred backdrop.
 *
 * Usage:
 *   {open && (
 *     <Modal onClose={() => setOpen(false)} testid="my-modal" widthClass="max-w-3xl">
 *       ...children...
 *     </Modal>
 *   )}
 */
export default function Modal({ children, onClose, testid, widthClass = 'max-w-3xl' }) {
  // Lock body scroll while open + close on Esc
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 sm:p-6"
      style={{ zIndex: 9999, background: 'rgba(15, 15, 18, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid={testid ? `${testid}-backdrop` : undefined}
    >
      <div
        data-testid={testid}
        className={`v-card w-full ${widthClass} max-h-[90vh] overflow-hidden flex flex-col fade-up`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
